import React, { useEffect, useState } from 'react'
import { useHistory } from 'react-router-dom'

import {
  AwaitedNotification,
  DataType,
  Form,
  FormOptions,
  Section,
  SelectOption,
  useNotify
} from '@globaleris/ui.library'
import { makeStyles, useTheme } from '@material-ui/core'
import { useTranslation } from 'react-i18next'

import Page from '../../../components/page/page'
import PageHeader from '../../../components/page/pageHeader'

import {
  enumToSelectOptionArray,
  enumToSelectOptionArrayDefaultValues,
  enumValueToKey
} from '../../../helpers'

import { getCustomerDomain, notifyError, setIsLoading } from '../../../redux/helpers'

import { EnumCommodity } from '../../../api'
import { GlobalNotification, NonAuthRoutes } from '../../../model'
import { NewPriceModel, NewPriceModelResponse } from '../pricing.Model'

import { useConfirmationDialog, useHttpError } from '../../../hooks'

import {
  add,
  checkPriceCodeRepeated,
  fetchPricingListItems,
  recalculatePricesValues
} from '../pricing.service'

import { EnumMarketType } from '../../../api/Argentia/enums/marketType'
import { EnumPriceCalculationType } from '../../../api/Argentia/enums/priceCalculationType'
import { EnumRedondeo } from '../../../api/Argentia/enums/redondeo'
import { EnumPrecioUnitario } from '../../../api/oData/enums/enumPrecioUnitario'
import { EnumPriceType } from '../../../api/oData/enums/enumPriceType'
import { PriceExpressionValidator } from '../../../components/shared'
import { missingFieldsForm } from '../../../helpers/validateFormHelper'
import { ConfirmationDialogs } from '../../../model/enums/confirmationDialogs'
import { futurePricesMapperToDropdown } from '../pricing.mapper'
import { styles } from './pricingAdd.styles'

export interface PricingTableProps {
  priceId: number
}

export interface formDependencies {
  id?: number
  code?: string
  description?: string
  precioUnitario?: number
  marketType?: number
  commodity: number
  priceType: number
  redondeo?: number
  relatedFuturePrice?: number | null
  priceExpression?: string
  priceCalculationType?: EnumPriceCalculationType | null
}

export const PricingAddPage: React.FC<any> = props => {
  const history = useHistory()
  const [t] = useTranslation([
    'pricing',
    'pricingDetails',
    'pricingAdd',
    'globalerisLibrary',
    'enums',
    'sharedTranslations'
  ])

  const currentTheme = useTheme()
  const useStyles = makeStyles(styles(currentTheme))

  const { commodity, priceType } = props.location.state

  const [priceProperties, setPriceProperties] = useState<NewPriceModel>({
    commodity: commodity,
    priceType: priceType
  })

  const [prevProp, setPrevProp] = useState<NewPriceModel>({
    commodity: commodity,
    priceType: priceType
  })

  const [formState, setFormState] = useState<any>({})

  const [expressionValidated, setExpressionValidated] = useState<boolean>(false)
  const [priceExpression, setPriceExpression] = useState<string | undefined>(undefined)
  const [priceUnitSelected, setPriceUnitSelected] = useState<EnumPrecioUnitario | undefined>(
    undefined
  )
  const [priceCalculationTypeSelected, setPriceCalculationTypeSelected] = useState<
    EnumPriceCalculationType | undefined
  >(undefined)
  const [futurePrices, setFuturePrices] = useState<SelectOption[]>([])
  const [showValidator, setShowValidator] = useState<boolean>(
    priceType === EnumPriceType.IndiceCalculado || priceType === EnumPriceType.FormulaBase
      ? true
      : false
  )
  const [codeIsValid, setCodeIsValid] = useState<boolean | undefined>(true)
  const [regexError, setRegexError] = useState<boolean>(false)

  const [formTitle, setFormTitle] = useState<any>('')

  const [globalNotification, setGlobalNotification] = useState<GlobalNotification | undefined>(
    undefined
  )

  const { getConfirmation, closeConfirmation } = useConfirmationDialog()
  const { info, awaited } = useNotify()
  const { errorProps } = useHttpError()

  const showExpressionValidator = priceType => {
    if (priceType === EnumPriceType.IndiceCalculado || priceType === EnumPriceType.FormulaBase) {
      setShowValidator(true)
      setPriceProperties({ ...priceProperties, precioUnitario: undefined })
    } else {
      setShowValidator(false)
      setPriceCalculationTypeSelected(undefined)
      setPriceExpression(undefined)
      setPriceUnitSelected(undefined)
    }
  }

  const isCodeFormatValid = (code: string): boolean => {
    const regex = /^[\w%_áéíóúñÁÉÍÓÚÑ]+$/
    return regex.test(code)
  }

  const validateCode = async (priceCode: string) => {
    if (!isCodeFormatValid(priceCode)) {
      setRegexError(true)
      return
    } else {
      setRegexError(false)
    }

    const response = await checkPriceCodeRepeated(priceCode)
    if (response.success) {
      if (response.data.isValid) {
        setCodeIsValid(true)
      } else setCodeIsValid(false)
    } else {
      notifyError(t('pricingAdd:notifications.checkCode.error'))
      setCodeIsValid(undefined)
      // error 502
    }
  }

  // Handlers Starts

  const onGlobalNotificationChangeHandler = (value: GlobalNotification | undefined) => {
    setGlobalNotification(value)
  }

  const onFormSaveHandler = async (formData: any): Promise<boolean> => {
    if (showValidator) {
      // When click on Save + case with ExpressionValidator component
      if (formData && missingFieldsForm(addFormSections, formData, ['precioUnitario'])) {
        notifyError(t('pricingAdd:notifications.create.errorInvalidForm'))
        return false
      }
      if (formData && expressionValidated && priceUnitSelected && priceCalculationTypeSelected) {
        return await postPrice(formData)
      } else {
        // if formData = undefined means user clicked on Cancel button => Go back to pricing homepage.
        if (formData === undefined) {
          history.push(NonAuthRoutes.pricing)
          // if formData != undefined means user clicked on Save button but failed first validation linkedin to ExpressionValidator inputs.
        } else {
          priceUnitSelected ?? notifyError(t('pricingAdd:notifications.create.errorPriceUnit'))
          !expressionValidated && notifyError(t('pricingAdd:notifications.create.errorExpression'))
        }
        return false
      }
    } else if (formData) {
      // When click on Save + case without ExpressionValidator component
      if (missingFieldsForm(addFormSections, formData)) {
        notifyError(t('pricingAdd:notifications.create.errorInvalidForm'))
        return false
      }
      return await postPrice(formData)
    } else {
      // When click on Cancel button
      history.push(NonAuthRoutes.pricing)
      return false
    }
  }

  // Handlers Ends

  // Form Definition Starts

  const addFormSections: Section[] = [
    {
      inputs: [
        {
          name: 'code',
          label: t('pricing:forms.add.inputs.code'),
          type: DataType.TEXT,
          maxLength: 56,
          required: true,
          onBlur: value => {
            const code = value.toString()
            validateCode(code)
            setPriceProperties({ ...priceProperties, code })
          },
          errorState: (!codeIsValid && typeof codeIsValid !== 'undefined') || regexError,
          errorText: !codeIsValid
            ? t('pricingAdd:notifications.checkCode.notValid')
            : t('sharedTranslations:priceCode.errorText')
        },
        {
          name: 'description',
          label: t('pricing:forms.add.inputs.description'),
          type: DataType.TEXT,
          maxLength: 200,
          clearable: true
        },
        {
          name: 'priceType',
          label: t('pricing:forms.add.inputs.priceType'),
          selectOptions: enumToSelectOptionArrayDefaultValues(
            EnumPriceType,
            'enums:EnumPriceTypes',
            [
              EnumPriceType.Cotizacion,
              EnumPriceType.IndiceCalculado,
              EnumPriceType.Parametro,
              EnumPriceType.FormulaBase,
              EnumPriceType.Apuntamiento,
              EnumPriceType.ParametroPeriodo
            ]
          ),
          type: DataType.SELECT,
          required: true,
          onChange: (value, formState: any) => {
            if (!showValidator) {
              showExpressionValidator(formState.priceType)
              if (
                formState.priceType === EnumPriceType.IndiceCalculado ||
                formState.priceType === EnumPriceType.FormulaBase
              ) {
                setPriceProperties({
                  ...formState,
                  priceType: formState.priceType,
                  precioUnitario: undefined
                })
              } else {
                setPriceProperties({
                  ...formState,
                  priceType: formState.priceType
                })
              }
              // We need to update the prevProp state to the new priceType.
              setPrevProp({ ...prevProp, priceType: formState.priceType })
              return
            } else {
              getConfirmation(ConfirmationDialogs.CHANGE_COMMODITY).then(async () => {
                showExpressionValidator(formState.priceType)
                if (
                  formState.priceType === EnumPriceType.IndiceCalculado ||
                  formState.priceType === EnumPriceType.FormulaBase
                ) {
                  setPriceProperties({
                    ...formState,
                    priceType: formState.priceType,
                    precioUnitario: undefined
                  })
                } else {
                  setPriceProperties({
                    ...formState,
                    priceType: formState.priceType
                  })
                }
                setPrevProp({ ...prevProp, priceType: formState.priceType })
                closeConfirmation()
                return
              })
              // If user hit CANCEL, we restablish the previous priceType value:
              setPriceProperties({
                ...formState,
                priceType: prevProp.priceType
              })
            }
          }
        },
        {
          name: 'commodity',
          label: t('pricing:forms.add.inputs.commodity'),
          selectOptions: enumToSelectOptionArray(EnumCommodity, 'enums:EnumCommodity'),
          type: DataType.SELECT,
          required: true,
          onChange: (value, formState: any) => {
            getConfirmation(ConfirmationDialogs.CHANGE_COMMODITY).then(async () => {
              setPriceProperties({
                ...formState,
                commodity: formState.commodity,
                marketType:
                  formState.marketType === EnumMarketType.Horario ? undefined : formState.marketType
              })
              setPrevProp({ ...prevProp, commodity: formState.commodity })
              closeConfirmation()
              return
            })
            // If user hit CANCEL, we restablish the previous priceType value:
            setPriceProperties({
              ...formState,
              commodity: prevProp.commodity
            })
          }
        },
        {
          name: 'precioUnitario',
          label: t('pricing:forms.add.inputs.priceUnit'),
          type: DataType.SELECT,
          selectOptions: enumToSelectOptionArray(EnumPrecioUnitario, 'enums:CurrencyAndUnit'),
          display:
            priceProperties?.priceType !== EnumPriceType.IndiceCalculado &&
            priceProperties?.priceType !== EnumPriceType.FormulaBase,
          required: true
        },
        {
          name: 'redondeo',
          label: t('pricing:forms.add.inputs.rounding'),
          type: DataType.SELECT,
          selectOptions: enumToSelectOptionArray(EnumRedondeo, 'enums:EnumRedondeo'),
          required: true
        },
        {
          name: 'marketType',
          label: t('pricing:forms.add.inputs.productType'),
          selectOptions:
            // For commodity Gas, hourly marketType option does not apply.
            priceProperties.commodity === EnumCommodity.GasNatural
              ? enumToSelectOptionArrayDefaultValues(EnumMarketType, 'enums:EnumMarketType', [
                  EnumMarketType.Diario,
                  EnumMarketType.Laborables,
                  EnumMarketType.Mensual,
                  EnumMarketType.Trimestral
                ])
              : enumToSelectOptionArray(EnumMarketType, 'enums:EnumMarketType'),
          type: DataType.SELECT,
          display:
            priceProperties?.priceType === EnumPriceType.Cotizacion ||
            priceProperties?.priceType === EnumPriceType.Futuro,
          clearable: true
        }
        // {
        //   name: 'relatedFuturePrice',
        //   label: t('pricing:forms.add.inputs.relatedFuturePrice'),
        //   type: DataType.SELECT,
        //   selectOptions: futurePrices,
        //   display: priceProperties?.priceType === PriceTypes.cotization,
        //   clearable: true
        // }
      ]
    }
  ]

  const options: FormOptions = {
    elevation: 1,
    columns: 4,
    saveDisabled: !codeIsValid || regexError,
    onSave: data => onFormSaveHandler(data)
  }

  // Form Config Ends

  // DataTable Config Starts

  // DataTable Config Ends

  // Functions Starts
  const handleRecalculateValues = async (price: NewPriceModelResponse) => {
    awaited({
      title: t('pricingDetails:notifications.recalculatePrices.title', {
        priceCode: price.code
      }),
      message: t('pricingDetails:notifications.recalculatePrices.message'),
      keyInfo: price!.id.toString()
    } as AwaitedNotification)

    recalculatePricesValues(price!.id)

    //Notifications ****************************************************************************************************/
    // const recalculate = (result: boolean, connectionId: string | null) => {
    //   if (result) {
    //     console.log(connectionId);
    //     recalculatePricesValues(price!.id, connectionId);
    //   } else {
    //     console.log(`No ha conectado, ${connectionId}`);
    //   }
    // };

    // awaited(
    //   t('pricingDetails:notifications.recalculatePrices.message'),
    //   t('pricingDetails:notifications.recalculatePrices.title', {
    //     priceCode: price.code
    //   }),
    //   undefined,
    //   { onConnectedHandler: recalculate }
    // );
    //Notifications ****************************************************************************************************/

    setIsLoading(false)
  }

  const postPrice = async (formData): Promise<boolean> => {
    return await add(
      formData,
      formData.commodity,
      formData.priceType,
      priceExpression,
      priceUnitSelected,
      priceCalculationTypeSelected
    ).then(response => {
      if (response.success) {
        const newPrice: NewPriceModelResponse = response.data
        if (
          enumValueToKey(EnumPriceType, newPrice.priceType) === EnumPriceType.IndiceCalculado ||
          enumValueToKey(EnumPriceType, newPrice.priceType) === EnumPriceType.FormulaBase
        ) {
          handleRecalculateValues(newPrice)
        }
        info(t('pricingAdd:notifications.create.success'))
        setTimeout(
          () =>
            history.push(NonAuthRoutes.pricingEdit, {
              priceId: newPrice.id,
              commodity: priceProperties.commodity,
              priceType: priceProperties.priceType
            }),
          2000
        )
        return true
      } else {
        notifyError(t('pricingAdd:notifications.create.error'))
        return true
      }
    })
  }

  const fetchFuturePricesOptions = async () => {
    await fetchPricingListItems(commodity, EnumPriceType.Futuro).then(response => {
      if (response.success) {
        setFuturePrices(futurePricesMapperToDropdown(response.data))
      } else {
        notifyError(t('pricingAdd:notifications.fetchFutures.error'))
      }
    })
  }

  // Functions Ends

  // Hooks Starts

  useEffect(() => {
    fetchFuturePricesOptions()
  }, [])

  useEffect(() => {
    setFormTitle(formState.code)
  }, [formState])

  // Hooks Ends

  return (
    <Page notification={globalNotification} error={errorProps}>
      <PageHeader title={t('pricingAdd:title')}></PageHeader>
      {priceProperties && (
        <>
          <Form
            sections={addFormSections}
            data={priceProperties}
            options={{
              ...options,
              analytics: {
                customer: getCustomerDomain(),
                view: t('pricingAdd:forms.add.analytics')
              }
            }}
          />
          <PriceExpressionValidator
            showPriceUnit
            showVersion
            show={showValidator}
            priceType={priceProperties.priceType}
            commodity={priceProperties.commodity}
            onIsValid={isValid => {
              setExpressionValidated(isValid)
            }}
            onSelectValidExpression={expression => showValidator && setPriceExpression(expression)}
            onSelectPriceCalculationType={selected =>
              showValidator && setPriceCalculationTypeSelected(selected)
            }
            onSelectPriceUnit={selected => showValidator && setPriceUnitSelected(selected)}
            priceVersion={1}
            activePriceCalculationType={2}
          />
        </>
      )}
    </Page>
  )
}
